/*
 * @Descripttion:
 * @Author: Junteng Shi
 * @Date: 2025-03-06 14:58:24
 * @LastEditors: HongJian Liu
 * @LastEditTime: 2025-07-29 11:33:07
 */
import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { showMessage } from './status';
import { IResponse } from './type';
import { getToken } from '@/utils/auth';
import { useUserStore } from '@/store';

const win: Win = window;

const pendingRequests = new Map();

function generateKey(config) {
  return config.url + JSON.stringify(config.params || {}) + JSON.stringify(config.data || {});
}

const service: AxiosInstance = axios.create({
  baseURL: import.meta.env.PROD ? win.VITE_APP_API_BASEURL : import.meta.env.VITE_APP_API_BASEURL,
  timeout: 10000,
});

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = token;
    }

    const key = generateKey(config);

    // 判断是否已有相同请求正在进行
    if (pendingRequests.has(key)) {
      ElMessage.warning('请勿重复请求');
      // 使用 CancelToken 取消重复请求
      const cancelToken = axios.CancelToken;
      const source = cancelToken.source();
      config.cancelToken = source.token;
      source.cancel('重复请求');
      return Promise.reject(new axios.Cancel('重复请求'));
    }

    // 标记该请求已发起
    pendingRequests.set(key, true);

    // 设置定时器，在一定时间后自动释放该请求标识（避免长时间占用）
    setTimeout(() => {
      pendingRequests.delete(key);
    }, 1000); // 设置防重时间窗口为 1 秒

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const key = generateKey(response.config);
    pendingRequests.delete(key); // 请求成功后清除标记

    if (response.status === 200) {
      // 兼容blob响应返回json数据
      if (response.data instanceof Blob && response.data.type.includes('application/json')) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsText(response.data, 'utf-8');
          reader.onload = () => {
            try {
              const json = JSON.parse(reader.result as string);
              console.log('响应数据:', response, json);
              resolve(json);
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = (error) => {
            reject(error);
          };
        });
      }

      if (!response.headers['content-type'].includes('application/json')) {
        return response.data;
      }

      const { code, msg, message, data } = response.data;
      switch (code) {
        case 200:
          return data || response.data;
        case 401:
          handleUnauthorized(msg || message);
          break;
        default:
          ElMessage.error(msg || message || 'Error');
          break;
      }
      return Promise.reject(new Error(msg || message || 'Error'));
    }

    handleError(response.status);
    return Promise.reject(new Error(showMessage(response.status)));
  },
  (error) => {
    const { response } = error;
    if (response) {
      const key = generateKey(response.config);
      pendingRequests.delete(key); // 请求失败后也清除标记

      handleError(response.status);
      return Promise.reject(response.data);
    }

    ElMessage.error('网络连接异常,请稍后再试!');
    return Promise.reject(new Error('网络连接异常,请稍后再试!'));
  },
);
// 处理未授权逻辑
function handleUnauthorized(message: string) {
  useUserStore().logout();
  ElMessage.error(message || 'Unauthorized');
}

// 处理错误状态码
function handleError(status: number) {
  ElMessage.error(showMessage(status));
}

// 通用请求方法
const request = async <T = any>(config: AxiosRequestConfig): Promise<T> => {
  try {
    const response = await service.request<IResponse>(config);
    // console.log(response);
    return response as unknown as T;
  } catch (error) {
    return Promise.reject(error);
  }
};

// HTTP 方法封装
export const get = <T = any>(config: AxiosRequestConfig): Promise<T> => request({ ...config, method: 'GET' });
export const post = <T = any>(config: AxiosRequestConfig): Promise<T> => request({ ...config, method: 'POST' });
export const put = <T = any>(config: AxiosRequestConfig): Promise<T> => request({ ...config, method: 'PUT' });
export const del = <T = any>(config: AxiosRequestConfig): Promise<T> => request({ ...config, method: 'DELETE' });

export default request;
